import { useMemo, useState } from "react";

import type { DraftValidation, ValidationIssue } from "./types";

type ValidationReport = NonNullable<DraftValidation["report"]>;
type SeverityFilter = ValidationIssue["severity"] | "all";

type IssueGroup = {
  key: string;
  issues: ValidationIssue[];
  severity: ValidationIssue["severity"];
  title: string;
};

const PAGE_SIZE = 50;
const severityOrder = { error: 0, warning: 1, information: 2 };

export function ValidationReportView({ report, sourceRevision }: {
  report: ValidationReport;
  sourceRevision: number;
}) {
  const [severity, setSeverity] = useState<SeverityFilter>("all");
  const [source, setSource] = useState("all");
  const [sheet, setSheet] = useState("all");
  const [visibleByGroup, setVisibleByGroup] = useState<Record<string, number>>({});
  const sourceNames = useMemo(
    () => uniqueValues(report.issues.map((issue) => issue.sourceName || issue.sourceId)),
    [report.issues],
  );
  const sheetNames = useMemo(
    () => uniqueValues(report.issues.map((issue) => issue.sheet)),
    [report.issues],
  );
  const groups = useMemo(() => {
    const filtered = report.issues.filter((issue) => {
      if (severity !== "all" && issue.severity !== severity) return false;
      if (source !== "all" && (issue.sourceName || issue.sourceId) !== source) return false;
      if (sheet !== "all" && issue.sheet !== sheet) return false;
      return true;
    });
    return groupIssues(filtered);
  }, [report.issues, severity, sheet, source]);

  const downloadCsv = () => {
    const blob = new Blob([validationReportCsv(report)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rapport-validation-r${sourceRevision}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return <div className="dm-validation-report">
    <div className="dm-report-summary" aria-label="Résumé de validation">
      <ReportMetric label="Erreurs bloquantes" value={report.summary.errors} tone="error" />
      <ReportMetric label="Avertissements" value={report.summary.warnings} tone="warning" />
      <ReportMetric label="Informations" value={report.summary.information} tone="information" />
      {report.summary.affectedSources !== undefined ? <ReportMetric label="Fichiers concernés" value={report.summary.affectedSources} /> : null}
      {report.summary.affectedSheets !== undefined ? <ReportMetric label="Feuilles concernées" value={report.summary.affectedSheets} /> : null}
    </div>
    <p className={report.summary.errors ? "dm-report-blocking" : "dm-report-valid"}>
      {report.summary.errors
        ? "La validation est bloquée tant que les erreurs ne sont pas corrigées."
        : "Aucune erreur bloquante n’a été détectée."}
    </p>
    {report.archiveInventories?.map((inventory) => <details key={inventory.sourceId} className="dm-report-archive">
      <summary>Archive analysée · {inventory.validXlsx ?? 0} Excel valide(s) · {inventory.validCsv} CSV valide(s) sur {inventory.detected} entrée(s)</summary>
      <p>{inventory.ignored.length} ignorée(s) · {inventory.invalid.length} invalide(s) · {inventory.readErrors?.length ?? 0} erreur(s) de lecture · {inventory.associationErrors?.length ?? 0} erreur(s) d’association</p>
      {inventory.paths.length ? <ul>{inventory.paths.map((path) => <li key={path}>{path}</li>)}</ul> : null}
    </details>)}
    <div className="dm-report-toolbar">
      <label>Gravité<select value={severity} onChange={(event) => setSeverity(event.target.value as SeverityFilter)}>
        <option value="all">Toutes</option><option value="error">Erreurs</option><option value="warning">Avertissements</option><option value="information">Informations</option>
      </select></label>
      <label>Fichier<select value={source} onChange={(event) => setSource(event.target.value)}>
        <option value="all">Tous</option>{sourceNames.map((name) => <option key={name} value={name}>{name}</option>)}
      </select></label>
      <label>Feuille<select value={sheet} onChange={(event) => setSheet(event.target.value)}>
        <option value="all">Toutes</option>{sheetNames.map((name) => <option key={name} value={name}>{name}</option>)}
      </select></label>
      <button className="btn btn-outline" type="button" onClick={downloadCsv}>Exporter le rapport CSV</button>
    </div>
    {groups.length ? <div className="dm-issue-groups">
      {groups.map((group, index) => {
        const visible = visibleByGroup[group.key] ?? PAGE_SIZE;
        const excelFilter = isExcelFilter(group.issues[0]);
        return <details key={group.key} open={groups.length === 1 || index === 0} className={`dm-issue-group dm-issue-${group.severity}`}>
          <summary><SeverityLabel severity={group.severity} /> <strong>{group.title}</strong> <span>{group.issues.length} occurrence(s)</span></summary>
          <div className="dm-issue-group-body">
            <IssueDetails issue={group.issues[0]} sourceRevision={sourceRevision} />
            {group.issues.length > 1 || issueLocation(group.issues[0]) ? <table className="dm-issue-locations">
              <thead><tr><th>Fichier</th><th>Feuille</th><th>Emplacement</th><th>{excelFilter ? "Critère" : "Valeur trouvée"}</th></tr></thead>
              <tbody>{group.issues.slice(0, visible).map((issue, issueIndex) => <tr key={`${group.key}-${issueIndex}`}>
                <td>{issue.sourceName || issue.sourceId || "Rapport global"}</td>
                <td>{issue.sheet || "—"}</td>
                <td>{issueLocation(issue) || "—"}</td>
                <td>{excelFilter ? filterCriterion(issue) : displayValue(issue.value)}</td>
              </tr>)}</tbody>
            </table> : null}
            {visible < group.issues.length ? <button className="btn btn-outline" type="button" onClick={() => setVisibleByGroup((current) => ({ ...current, [group.key]: visible + PAGE_SIZE }))}>Afficher 50 occurrences supplémentaires</button> : null}
          </div>
        </details>;
      })}
    </div> : <p>Aucune anomalie pour ces filtres.</p>}
    {report.truncatedIssueCount ? <p className="dm-report-truncated">Le rapport conserve {report.issues.length} anomalie(s). {report.truncatedIssueCount} anomalie(s) supplémentaire(s) n’ont pas été incluses à cause de la limite serveur.</p> : null}
  </div>;
}

function IssueDetails({ issue, sourceRevision }: { issue: ValidationIssue; sourceRevision: number }) {
  const sourceParameters = new URLSearchParams({ section: "sources", revision: `${sourceRevision}` });
  if (issue.sourceId) sourceParameters.set("source", issue.sourceId);
  if (issue.sheet) sourceParameters.set("sheet", issue.sheet);
  if (isExcelFilter(issue)) {
    return <div className="dm-issue-description dm-excel-filter-description">
      <p>{issue.message}</p>
      <dl className="dm-excel-filter-location">
        <div><dt>Feuille</dt><dd>{issue.sheet || "Non précisée"}</dd></div>
        <div><dt>Colonne</dt><dd>{filterColumnLabel(issue)}</dd></div>
        {issue.filterRange ? <div><dt>Plage du filtre</dt><dd>{issue.filterRange}</dd></div> : null}
        {issue.affectedRange ? <div><dt>Plage de cette colonne</dt><dd>{issue.affectedRange}</dd></div> : null}
      </dl>
      <p><strong>Critère actuel :</strong> {filterCriterion(issue)}</p>
      {issue.expected ? <p><strong>Attendu :</strong> {issue.expected}</p> : null}
      {issue.suggestion ? <p><strong>Correction :</strong> {issue.suggestion}</p> : null}
      <p className="dm-excel-filter-note">Cette erreur concerne le filtre Excel de la colonne, pas une ligne de données particulière.</p>
      {issue.sourceId ? <p><a href={`?${sourceParameters.toString()}`}>Voir le fichier dans Sources</a></p> : null}
      <p className="dm-issue-technical">Code technique : <code>{issue.code}</code>{issue.rule ? ` · Règle : ${issue.rule}` : ""}</p>
    </div>;
  }
  return <div className="dm-issue-description">
    <p>{issue.message}</p>
    {issue.value !== undefined ? <p><strong>Valeur trouvée :</strong> {displayValue(issue.value)}</p> : null}
    {issue.expected ? <p><strong>Valeur attendue :</strong> {issue.expected}</p> : null}
    {issue.suggestion ? <p><strong>Correction proposée :</strong> {issue.suggestion}</p> : null}
    {issue.formula ? <p><strong>Formule :</strong> <code>{issue.formula}</code></p> : null}
    {issue.sourceId ? <p><a href={`?${sourceParameters.toString()}`}>Voir le fichier dans Sources</a></p> : null}
    <p className="dm-issue-technical">Code technique : <code>{issue.code}</code>{issue.rule ? ` · Règle : ${issue.rule}` : ""}</p>
  </div>;
}

function ReportMetric({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return <div className={`dm-report-metric${tone ? ` dm-report-metric-${tone}` : ""}`}><strong>{value}</strong><span>{label}</span></div>;
}

function SeverityLabel({ severity }: { severity: ValidationIssue["severity"] }) {
  if (severity === "error") return <span className="dm-severity">Erreur</span>;
  if (severity === "warning") return <span className="dm-severity">Avertissement</span>;
  return <span className="dm-severity">Information</span>;
}

function uniqueValues(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].sort();
}

function groupIssues(issues: ValidationIssue[]): IssueGroup[] {
  const groups = new Map<string, IssueGroup>();
  for (const issue of issues) {
    const key = [issue.sourceName || issue.sourceId || "global", issue.sheet || "", issue.column || "", issue.locationType || "", issue.operator || "", issue.displayValue || "", issue.code, issue.message, issue.expected || "", issue.suggestion || ""].join("\u0000");
    const existing = groups.get(key);
    if (existing) {
      existing.issues.push(issue);
      continue;
    }
    groups.set(key, {
      key,
      issues: [issue],
      severity: issue.severity,
      title: issueTitle(issue),
    });
  }
  return [...groups.values()].sort((left, right) => {
    const severityDifference = severityOrder[left.severity] - severityOrder[right.severity];
    if (severityDifference) return severityDifference;
    return right.issues.length - left.issues.length;
  });
}

function issueTitle(issue: ValidationIssue): string {
  if (issue.title) return issue.title;
  if (issue.message.length <= 160) return issue.message;
  return `${issue.message.slice(0, 157)}…`;
}

function issueLocation(issue: ValidationIssue): string {
  if (isExcelFilter(issue)) {
    const column = filterColumnLabel(issue);
    if (issue.affectedRange) return `${column} · ${issue.affectedRange}`;
    return column;
  }
  if (issue.cell) return issue.cell;
  if (issue.row !== undefined && issue.column) return `${issue.column}, ligne ${issue.row}`;
  if (issue.row !== undefined) return `Ligne ${issue.row}`;
  if (issue.column) return `Colonne ${issue.column}`;
  if (issue.columnIndex !== undefined) return `Colonne ${issue.columnIndex}`;
  return "";
}

function displayValue(value: ValidationIssue["value"]): string {
  if (value === undefined) return "—";
  if (typeof value === "string") return displayInvisibleText(value);
  return `${value}`;
}

function displayInvisibleText(value: string): string {
  if (value === "") return "chaîne vide";
  if (/^ +$/.test(value)) {
    if (value.length === 1) return "espace";
    return `${value.length} espaces`;
  }
  if (value === "\t") return "tabulation";
  if (value === "\n" || value === "\r" || value === "\r\n") return "saut de ligne";
  return value;
}

function isExcelFilter(issue: ValidationIssue): boolean {
  if (issue.locationType === "excel_filter") return true;
  return issue.rule === "excel_custom_filter";
}

function filterColumnLabel(issue: ValidationIssue): string {
  const column = issue.column || "Non précisée";
  if (!issue.excelColumn) return column;
  return `${column} (${issue.excelColumn})`;
}

function filterCriterion(issue: ValidationIssue): string {
  const rawValue = issue.rawValue ?? issue.value;
  let value = issue.displayValue;
  if (value === undefined) value = displayValue(rawValue);
  const operator = filterOperatorLabel(issue.operator);
  if (!operator) return `« ${value} »`;
  return `${operator} « ${value} »`;
}

function filterOperatorLabel(operator: string | undefined): string {
  if (operator === "notEqual") return "différent de";
  if (operator === "equal") return "égal à";
  if (operator === "lessThan") return "inférieur à";
  if (operator === "lessThanOrEqual") return "inférieur ou égal à";
  if (operator === "greaterThan") return "supérieur à";
  if (operator === "greaterThanOrEqual") return "supérieur ou égal à";
  return "";
}

export function validationReportCsv(report: ValidationReport): string {
  const headers = ["gravite", "titre", "message", "fichier", "feuille", "ligne", "colonne", "cellule", "valeur_trouvee", "valeur_attendue", "suggestion", "code", "regle", "formule", "rapport_tronque", "anomalies_non_incluses", "type_emplacement", "colonne_excel", "plage_filtre", "plage_concernee", "operateur", "critere", "critere_affiche"];
  const rows = report.issues.map((issue) => [
    issue.severity, issue.title, issue.message, issue.sourceName || issue.sourceId, issue.sheet,
    issue.row, issue.column, issue.cell, issue.value, issue.expected, issue.suggestion, issue.code,
    issue.rule, issue.formula, report.truncatedIssueCount > 0, report.truncatedIssueCount,
    issue.locationType, issue.excelColumn, issue.filterRange, issue.affectedRange, issue.operator,
    issue.rawValue, issue.displayValue,
  ]);
  return `\uFEFF${[headers, ...rows].map((row) => row.map(csvCell).join(";")).join("\r\n")}`;
}

function csvCell(value: unknown): string {
  if (value === null || value === undefined) return "\"\"";
  let text = `${value}`;
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}
